<template>
  <b-overlay :show="show" :z-index="1061" rounded="sm" class="d-flex">
    <b-container fluid class="p-0">
      <div class="academy-layout d-flex">
        <!-- NavBar como sidebar a la izquierda -->
        <div class="sidebar">
          <NavBarAcademy />
        </div>

        <!-- Contenido principal a la derecha (ahora con clase bg-content) -->
        <div class="content">
          <!-- Sección de proyectos recomendados -->
          <b-row class="mt-3">
            <b-col>
              <div class="d-flex justify-content-between">
                <h4 class="mt-2 mb-3">{{ $t("home.title") }}</h4>
                <b-button
                  variant="link"
                  @click="navigateToRecommendedProjects"
                  >{{ $t("home.see-all") }}</b-button
                >
              </div>
              <b-card>
                <b-card-text class="text-muted pl-2" style="font-size: 22px">{{
                  $t("home.card1.title")
                }}</b-card-text>
                <b-row
                  class="row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-5 row-cols-xl-5"
                >
                  <!-- Card de Nuevo Proyecto -->
                  <b-col>
                    <b-card
                      class="mb-2 card-container card-container-radius btn-custom text-center p-5"
                      @click="showModalForCreate"
                    >
                      <span class="text-plus">+</span>
                    </b-card>
                    <b-card-text class="text-center">{{
                      $t("home.card1.option-1")
                    }}</b-card-text>
                  </b-col>

                  <!-- Proyectos recomendados recientes -->
                  <b-col
                    v-for="project in recentPublishedProjects"
                    :key="project.id"
                    @click="selectRecommendedProject(project)"
                  >
                    <b-card class="mb-2 card-container-projects section">
                      <b-card-text class="text-center p-0">
                        <div class="project-img-container">
                          <img
                            :src="project.image || noImageDefault"
                            alt="Imagen del proyecto"
                            class="project-img-default"
                          />
                        </div>
                      </b-card-text>
                      <div class="project-badge">
                        <b-badge
                          :variant="
                            getDifficultyBadgeVariant(project.difficulty)
                          "
                          class="mr-2"
                        >
                          {{ getDifficultyText(project.difficulty) }}
                        </b-badge>
                      </div>
                    </b-card>
                    <b-card-text class="text-center mb-1">{{
                      project.name
                    }}</b-card-text>
                    <div class="project-badges">
                      <b-badge variant="primary" class="mr-1">{{
                        getCategoryDisplayName(project.category)
                      }}</b-badge>
                      <b-badge variant="success"
                        >{{ project.estimatedTime }} min</b-badge
                      >
                    </div>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>

          <!-- Sección de mis proyectos -->
          <b-row class="mb-3 mt-4">
            <b-col>
              <div class="d-flex justify-content-between">
                <h4>{{ $t("projects.title") }}</h4>
                <b-button variant="link" @click="navigateToAllProjects">{{
                  $t("home.see-all")
                }}</b-button>
              </div>
              <b-card class="mb-3" v-if="firstFiveProjects.length > 0">
                <b-row
                  class="row-cols-1 row-cols-sm-1 row-cols-md-2 row-cols-lg-5 row-cols-xl-5"
                >
                  <b-col
                    v-for="project in firstFiveProjects"
                    :key="project.id"
                    @click="selectProjectAndGoToCanvas(project.id)"
                  >
                    <b-card class="mb-2 card-container-projects section">
                      <b-card-text class="text-center p-0">
                        <div class="project-img-container">
                          <img
                            :src="project.imageUrl || noImageDefault"
                            alt="Imagen del proyecto"
                            class="project-img-default"
                          />
                        </div>
                      </b-card-text>
                      <div class="context-button">
                        <b-dropdown
                          size="lg"
                          dropright
                          variant="none"
                          toggle-class="text-decoration-none"
                          no-caret
                        >
                          <template #button-content>
                            <dots-horizontal-icon />
                          </template>
                          <b-dropdown-item
                            @click.stop="showModalForUpdateName(project)"
                            >{{ $t("home.change-name") }}</b-dropdown-item
                          >
                          <b-dropdown-item
                            @click.stop="openProjectInNewTab(project.id)"
                            >{{ $t("home.open-new-tab") }}</b-dropdown-item
                          >
                          <b-dropdown-item @click.stop="showModalForUpdateImage(project)">{{$t('home.change-image')}}</b-dropdown-item>
                          <b-dropdown-item
                            @click.stop="handleDuplication(project)"
                            >{{ $t("home.duplicate-project") }}</b-dropdown-item
                          >
                          <b-dropdown-item
                            @click.stop="removeProject(project.id)"
                            >{{ $t("home.delete-project") }}</b-dropdown-item
                          >
                        </b-dropdown>
                      </div>
                    </b-card>
                    <b-card-text class="text-center">{{
                      project.name
                    }}</b-card-text>
                  </b-col>
                </b-row>
              </b-card>
              <b-card class="mb-3 text-center" v-else>
                <h3>{{ $t("home.no-projects") }}</h3>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-container>

    <!-- Modal para crear/actualizar proyecto -->
    <b-modal
      v-model="showModal"
      centered
      id="modal-center"
      :title="
        isUpdateMode
          ? updateType === 'name'
            ? $t('home.change-name')
            : $t('home.change-image')
          : $t('home.modal.create')
      "
    >
      <!-- Formulario para crear o actualizar -->
      <b-form @submit.prevent="isUpdateMode ? handleUpdate : insertProject">
        <!-- Campo para el nombre -->
        <b-form-group
          v-if="!isUpdateMode || updateType === 'name'"
          :label="$t('home.modal.label-name')"
          label-for="name-input"
        >
          <b-form-input
            id="name-input"
            v-model="newProjectName"
            :placeholder="$t('home.modal.placeholder-name')"
            required
          ></b-form-input>
        </b-form-group>

        <!-- Campo para la categoría (solo al crear) -->
        <b-form-group
          v-if="!isUpdateMode"
          :label="$t('home.modal.label-select')"
          label-for="categories"
        >
          <b-form-select
            id="categories"
            v-model="selectedCategory"
            :options="categories"
          ></b-form-select>
        </b-form-group>

        <b-form-group
          v-if="!isUpdateMode || updateType === 'image'"
          :label="$t('home.project-image') + (!isUpdateMode ? ' (opcional)' : '')"
          label-for="image-input"
        >
          <b-form-file
            accept="image/jpeg, image/png"
            id="image-input"
            @change="handleFileUpload"
            :placeholder="$t('home.select-image')"
          ></b-form-file>
        </b-form-group>
      </b-form>

      <!-- Botones en el footer -->
      <template v-slot:modal-footer>
        <div class="w-100">
          <button
            type="button"
            @click="isUpdateMode ? handleUpdate() : insertProject()"
            class="btn btn-primary float-right m-1"
          >
            {{
              isUpdateMode
                ? $t("home.modal.btn-update")
                : $t("home.modal.btn-ok")
            }}
          </button>
          <button @click="closeModal" class="btn btn-secondary float-right m-1">
            {{ $t("home.modal.btn-cancel") }}
          </button>
        </div>
      </template>
    </b-modal>
  </b-overlay>
</template>

<script>
/* Script permanece sin cambios */
import { mapActions, mapGetters } from "vuex";
import DotsHorizontalIcon from "vue-material-design-icons/DotsHorizontal.vue";
import noImageDefault from "@/assets/imgs/logoInventechDefault.png";
import NavBarAcademy from "@/components/academy/NavBarAcademy.vue";
import Swal from "sweetalert2";
import practicalGuideData from "@/data/practicalGuideData";

export default {
  name: "HomeView",
  components: {
    DotsHorizontalIcon,
    NavBarAcademy,
  },
  data() {
    return {
      show: false,
      showModal: false,
      showModalImage: false,
      isUpdateMode: false,
      currentProject: null,
      unsubscribe: null,
      newProjectName: "",
      categories: [
        { value: "robotics", text: this.$t("home.modal.option1") },
        { value: "iot", text: this.$t("home.modal.option2") },
        { value: "videogames", text: this.$t("home.modal.option3") },
        { value: "ia", text: this.$t("home.modal.option4") },
        { value: "other", text: this.$t("home.modal.option5") },
      ],
      selectedCategory: null,
      imageFile: null,
      noImageDefault,
      updateType: "",
      publishedGuides: [],
      recentPublishedProjects: [],
    };
  },
  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("projects", ["projects", "filteredProjects"]),
    firstFiveProjects() {
      const sorted = [...this.projects].sort((a, b) => {
        const dateA =
          a.createdAt && a.createdAt.toDate
            ? a.createdAt.toDate()
            : new Date(a.createdAt);
        const dateB =
          b.createdAt && b.createdAt.toDate
            ? b.createdAt.toDate()
            : new Date(b.createdAt);
        return dateB - dateA;
      });
      return sorted.slice(0, 5);
    },
  },

  mounted() {
    // 1. Carga los datos iniciales la primera vez que el componente se monta.
    this.loadRecentPublishedProjects();

    // 2. Se suscribe a los cambios en la base de datos.
    //    Cada vez que algo cambie (crear, actualizar, borrar), volverá a llamar a la función de carga.
    this.unsubscribe = practicalGuideData.subscribeToChanges(() => {
      console.log(
        "🔥 Actualización detectada en Firestore! Recargando proyectos..."
      );
      this.loadRecentPublishedProjects();
    });
  },

  beforeUnmount() {
    // 3. MUY IMPORTANTE: Se desuscribe de los cambios cuando el componente se destruye.
    //    Si no haces esto, tendrás fugas de memoria.
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },

  methods: {
    ...mapActions("projects", [
      "fetchProjects",
      "createProject",
      "deleteProject",
      "updateProject",
      "selectProject",
      "uploadProjectImage",
      "duplicateProjectWithData",
    ]),

    async loadPublishedGuides() {
      try {
        // Usamos la función optimizada para traer solo las guías publicadas.
        // El `false` es para asegurar que siempre traiga los datos más frescos,
        // ya que el listener nos avisa cuándo hacerlo.
        const publishedGuides = await practicalGuideData.getPublishedGuides(
          false
        );

        // Ordena por fecha de creación para obtener las más nuevas primero.
        const sortedGuides = publishedGuides.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });

        // Se queda solo con las primeras 4.
        this.recentPublishedProjects = sortedGuides.slice(0, 4);

        console.log(
          `✅ Proyectos recomendados cargados: ${this.recentPublishedProjects.length}`
        );
      } catch (error) {
        console.error("Error al cargar proyectos recomendados:", error);
        this.recentPublishedProjects = [];
      }
    },

    openProjectInNewTab(projectId) {
      const routeData = this.$router.resolve({
        name: "blank",
        params: { id: projectId },
      });
      window.open(routeData.href, "_blank");
    },

    // Cargar proyectos publicados recientemente
    async loadRecentPublishedProjects() {
      try {
        const allGuides = await practicalGuideData.getAllGuides();

        console.log("🚀 Cargando proyectos publicados recientemente...");
        console.log(`Total de guías obtenidas: ${allGuides.length}`);
        const publishedGuides = allGuides.filter(
          (guide) => guide.isPublished === true
        );

        // Ordenar por fecha de creación (más recientes primero)
        const sortedGuides = publishedGuides.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });

        // Tomar solo los primeros 4 (las 4 más recientes)
        this.recentPublishedProjects = sortedGuides.slice(0, 4);

        console.log(
          `HomeView - Proyectos recomendados cargados (4 más recientes): ${this.recentPublishedProjects.length}`
        );
      } catch (error) {
        console.error("Error al cargar proyectos recomendados:", error);
        this.recentPublishedProjects = [];
      }
    },

    // Métodos para manejar proyectos recomendados
    selectRecommendedProject(project) {
      const lang = this.$i18n.locale || "es";
      const url = `/${lang}/practical-guide/${project.id}`;
      window.open(url, "_blank");
    },

    navigateToRecommendedProjects() {
      this.$router.push({
        name: "recommended-projects",
        params: {
          lang: this.$i18n.locale || "es",
        },
      });
    },

    // Métodos para obtener información de dificultad
    getDifficultyBadgeVariant(difficulty) {
      const variants = {
        beginner: "success",
        intermediate: "warning",
        advanced: "danger",
      };
      return variants[difficulty] || "secondary";
    },

    getDifficultyText(difficulty) {
      const texts = {
        beginner: this.$t('recommended-projects.beginner'),
        intermediate: this.$t('recommended-projects.intermediate'),
        advanced: this.$t('recommended-projects.advanced'),
      };
      return texts[difficulty] || difficulty;
    },

    getCategoryDisplayName(category) {
      const categoryMap = {
        robotics: "Robótica",
        iot: "IoT",
        videogames: "Videojuegos",
        ia: "IA",
        other: "Otro",
      };
      return categoryMap[category] || category;
    },

    // Los métodos permanecen sin cambios...
    showModalForCreate() {
      this.isUpdateMode = false;
      this.newProjectName = "";
      this.selectedCategory = null;
      this.showModal = true;
    },
    showModalForUpdateName(project) {
      this.isUpdateMode = true;
      this.updateType = "name";
      this.currentProject = project;
      this.newProjectName = project.name;
      this.showModal = true;
    },
    showModalForUpdateImage(project) {
      this.isUpdateMode = true;
      this.updateType = "image";
      this.currentProject = project;
      this.showModal = true;
    },
    async handleUpdate() {
      if (this.updateType === "name") {
        await this.updateProjectName();
      } else if (this.updateType === "image") {
        await this.updateProjectImage();
      }
    },
    closeModal() {
      this.showModal = false;
      this.imageFile = null;
      this.newProjectName = "";
      this.selectedCategory = null;
      this.updateType = "";
      this.currentProject = null;
      this.isUpdateMode = false;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Validar tamaño del archivo
        if (file.size > 5 * 1024 * 1024) { // 5MB
          alert("El archivo es demasiado grande. Máximo 5MB.");
          return;
        }
        
        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
          alert("Por favor selecciona un archivo de imagen válido.");
          return;
        }
        
        this.imageFile = file;
        console.log("Archivo seleccionado:", file.name);
      }
    },
    async handleProjectDuplication(project) {
      this.show = true;
      try {
        await this.duplicateProject(project);
        this.show = false;
      } catch (error) {
        console.error("Error duplicating project:", error);
        this.show = false;
      }
    },
    async insertProject() {
      let imageUrl = "";

      // Subir imagen si existe
      if (this.imageFile) {
        try {
          this.show = true;
          imageUrl = await this.uploadProjectImage(this.imageFile);
        } catch (error) {
          this.show = false;
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Error al subir la imagen. Intenta nuevamente.",
          });
          return;
        }
      }

      const newProject = {
        name: this.newProjectName,
        category: this.selectedCategory,
        imageUrl,
      };

      // Validación
      if (!newProject.name || !newProject.category) {
        Swal.fire({
          icon: "error",
          title: "Campos requeridos",
          text: "Por favor completa el nombre y la categoría del proyecto.",
        });
        return;
      }

      try {
        this.show = true;
        
        // Crear proyecto
        const projectResult = await this.createProject(newProject);
        const projectId = projectResult.id || projectResult;
        console.log("✅ Proyecto creado con ID:", projectId);

        // IMPORTANTE: Refrescar lista de proyectos
        await this.fetchProjects();
        
        // Seleccionar proyecto
        await this.selectProject(projectId);

        // Cerrar modal y navegar
        this.closeModal();
        this.selectProjectAndGoToCanvas(projectId);
        
      } catch (error) {
        console.error("❌ Error creating project:", error);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: this.$t('academy.required_fields'),

        });
      } finally {
        this.show = false;
      }
    },
    async updateProjectName() {
      if (this.currentProject) {
        const updateData = { name: this.newProjectName };
        this.show = true;
        try {
          await this.updateProject({
            projectId: this.currentProject.id,
            updateData,
          });
          await this.fetchProjects();
          this.show = false;
          this.closeModal();
        } catch (error) {
          this.show = false;
          console.error("Error updating project name:", error);
        }
      }
    },
    async updateProjectImage() {
  if (!this.imageFile) {
    Swal.fire({
      icon: "warning",
      title: "Imagen requerida",
      text: "Debes seleccionar una imagen para actualizar.",
    });
    return;
  }

  try {
    this.show = true;
    
    // Subir nueva imagen
    const imageUrl = await this.uploadProjectImage(this.imageFile);
    console.log("✅ Nueva imagen subida:", imageUrl);
    
    // Actualizar proyecto
    const updateData = { imageUrl };
    await this.updateProject({
      projectId: this.currentProject.id,
      updateData,
    });
    
    // IMPORTANTE: Refrescar lista de proyectos
    await this.fetchProjects();
    
    console.log("✅ Proyecto actualizado correctamente");
    
    this.closeModal();
    
  } catch (error) {
    console.error("❌ Error updating image:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Error al actualizar la imagen. Intenta nuevamente.",
    });
  } finally {
    this.show = false;
  }
}
,
    selectProjectAndGoToCanvas(projectId) {
      // Siempre redirigir a la vista de board
      console.log("[DEBUG] Navegando a canvas con projectId:", projectId);
      this.$router.push({ name: "blank", params: { id: projectId } });
    },
    goToCanvas(id) {
      this.$router.push({ name: "blank", params: { id: id } });
    },
    navigateToAllProjects() {
      this.$router.push({ name: "all-projects" });
    },
    navigateToCreateProject(projectType) {
      this.$router.push({ name: "create-project", params: { projectType } });
    },
    async removeProject(projectId) {
      const result = await Swal.fire({
        title: this.$t("delete-project"),
        text: this.$t("academy.action-cannot-be-undone"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: this.$t("academy.yes-delete"),
        cancelButtonText: this.$t("academy.cancel"),
      });
      if (result.isConfirmed) {
        try {
          const updatedProjects = this.filteredProjects.filter(
            (project) => project.id !== projectId
          );
          this.$store.commit("projects/SET_FILTERED_PROJECTS", updatedProjects);
          Swal.fire({
            icon: "success",
            title: this.$t('academy.project_delete_success'),
            showConfirmButton: false,
            timer: 1500,
          });
          // Then perform the backend deletion
          await this.deleteProject(projectId);
        } catch (error) {
          console.error("Error al eliminar el proyecto", error);
          Swal.fire({
            icon: "error",
            title: this.$t("academy.error"),
            text: this.$t('academy.project_delete_error'),
          });
          await this.fetchProjects();
        }
      }
    },
    async duplicateProject(project) {
      this.show = true;
      try {
        await this.duplicateProjectWithData(project);
        this.show = false;
      } catch (error) {
        console.error("Error duplicating project:", error);
        this.show = false;
      }
    },
    async handleDuplication(project) {
      try {
        this.show = true;
        await this.duplicateProjectWithData(project);
      } catch (error) {
        console.error("Error al duplicar el proyecto:", error);
      } finally {
        this.show = false;
      }
    },
  },
  async created() {
    if (this.isAuthenticated) {
      this.show = true;
      try {
        await Promise.all([
          this.fetchProjects(),
          this.loadPublishedGuides(),
          this.loadRecentPublishedProjects(),
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.show = false;
      }
    } else {
      console.error("No user ID found.");
    }
  },

  // Recargar guías cuando se navega a esta vista
  async beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      if (vm.isAuthenticated) {
        console.log("HomeView - beforeRouteEnter: Recargando guías...");
        await vm.loadPublishedGuides();
        await vm.loadRecentPublishedProjects();
      }
    });
  },

  // También recargar cuando se navega desde otra vista
  async beforeRouteUpdate(to, from, next) {
    if (this.isAuthenticated) {
      await this.loadPublishedGuides();
      await this.loadRecentPublishedProjects();
    }
    next();
  },
};
</script>

<style scoped>
/* Estilos modificados para poner el contenido al fondo */
.academy-layout {
  display: flex;
  min-height: 100vh;
  position: relative;
}

.sidebar {
  flex: 0 0 auto;
  position: fixed; /* Cambiado de sticky a fixed */
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 10; /* Mayor z-index para que esté por encima */
  width: auto;
}

.content {
  flex-grow: 1;
  padding: 15px;
  margin-left: 228px; /* Ajusta este valor según el ancho de tu barra lateral */
  position: relative;
  z-index: 1;
}

.btn-custom {
  background-color: #76b8f1;
  border: 0;
  color: #fff;
  cursor: pointer;
}

.text-plus {
  font-size: 68px;
}

.section {
  cursor: pointer;
}

.btn:focus,
.btn.focus {
  box-shadow: 0 0 0 0;
}

.card-container-radius {
  border-radius: 25px;
}

.card-container-projects:hover {
  background-color: #d8eeff;
  transition: background-color 0.3s;
}

input {
  font-size: 14px;
}

input::placeholder {
  font-size: 14px;
}

select {
  font-size: 14px;
}

select option {
  font-size: 14px;
}

.context-button {
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  height: auto;
  min-width: 0;
}

.project-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.view-toggle-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn:focus,
.btn.focus {
  outline: 0;
}

.card-container:hover img[src*="no-image"] {
  filter: brightness(0) invert(1) sepia(1) hue-rotate(180deg);
}

.card-container:hover img:not([src*="no-image"]) {
  filter: none;
}

.project-img-container {
  width: 100%;
  height: 140px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.project-img-default {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.project-badges {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}

.project-badges .badge {
  font-size: 0.7rem;
  padding: 4px 8px;
}
</style>
